// Lightweight logger that logs in all environments except production.

type LogMethod = (...args: unknown[]) => void;

const isProduction = (): boolean => {
  try {
    return process.env.NODE_ENV === 'production';
  } catch (_err) {
    // In unlikely cases where process/env is unavailable, assume non-production
    return false;
  }
};

function createMethod(consoleMethod: LogMethod): LogMethod {
  return (...args: unknown[]) => {
    if (isProduction()) return;
    try {
      // Preserve native console formatting/objects
      consoleMethod(...args);
    } catch (_err) {
      // Last-resort safe log
      try {
        // eslint-disable-next-line no-console
        console.log('[logger]', ...args.map(a => safeToString(a)));
      } catch (_) {
        // swallow
      }
    }
  };
}

function safeToString(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch (_err) {
    return String(value);
  }
}

export const logger = {
  log: createMethod(console.log.bind(console)),
  info: createMethod(console.info.bind(console)),
  warn: createMethod(console.warn.bind(console)),
  error: createMethod(console.error.bind(console)),
  debug: createMethod((console as any).debug ? console.debug.bind(console) : console.log.bind(console)),
} as const;

export type Logger = typeof logger;


